import { normalizeAnalysisMarkdown } from './analysisMarkdownNormalizer.js';

const MARKDOWN_HEADING_RE = /^(#{1,6})\s+(.+?)\s*$/;

function resolveObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function resolveArray(value) {
  return Array.isArray(value) ? value : [];
}

function resolveText(value) {
  return String(value ?? '').trim();
}

function firstValue(values = []) {
  return resolveArray(values).find(Boolean);
}

function firstText(values = []) {
  return resolveText(firstValue(values));
}

function isObjectValue(value) {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

function firstObject(values = []) {
  return resolveArray(values)
    .map(resolveObject)
    .find((value) => Object.keys(value).length > 0) ?? {};
}

function normalizeOutlineItem(item, index) {
  const entry = resolveObject(item);
  const title = firstText([entry.title, entry.label, entry.heading]);
  if (!title) {
    return null;
  }
  const level = Number(firstValue([entry.level, entry.depth, 1]));
  return {
    id: firstText([entry.id, entry.key]) || `heading-${index + 1}`,
    level: Number.isFinite(level) ? level : 1,
    title,
  };
}

function normalizeOutlineItems(items = []) {
  return resolveArray(items).map(normalizeOutlineItem).filter(Boolean);
}

function normalizeOutline(value = {}) {
  const outline = resolveObject(value);
  const items = normalizeOutlineItems(firstValue([outline.items, outline.sections, outline.children]));
  if (items.length === 0) {
    return {};
  }
  return {
    ...outline,
    status: resolveText(outline.status) || 'ready',
    source: resolveText(outline.source) || 'analysis_projection',
    items,
  };
}

function outlineFromDocument(document = {}) {
  const source = resolveObject(document);
  const existingOutline = normalizeOutline(firstValue([source.outline, source.directory, source.toc]));
  if (Object.keys(existingOutline).length > 0) {
    return existingOutline;
  }
  return normalizeOutline({
    source: 'analysis_document_sections',
    items: resolveArray(source.sections),
  });
}

function outlineItemFromMarkdown(line, index) {
  const matched = resolveText(line).match(MARKDOWN_HEADING_RE);
  if (!matched) {
    return null;
  }
  return {
    id: `heading-${index + 1}`,
    level: matched[1].length,
    title: resolveText(matched[2]),
  };
}

function outlineFromMarkdown(markdown = '') {
  const items = String(markdown ?? '').split('\n')
    .map(outlineItemFromMarkdown)
    .filter((item) => item?.title);
  return normalizeOutline({ source: 'analysis_projection', items });
}

function normalizeStructureUnavailable(value = {}) {
  const unavailable = resolveObject(value);
  const reason = resolveText(unavailable.reason);
  if (!reason) {
    return {};
  }
  return {
    ...unavailable,
    reason,
    source: resolveText(unavailable.source) || 'analysis_projection',
  };
}

function resolveAnalysisOutline({ markdown = '', document = {}, payload = {} } = {}) {
  return firstObject([
    normalizeOutline(firstValue([payload.outline, payload.directory, payload.toc])),
    outlineFromDocument(document),
    outlineFromMarkdown(markdown),
  ]);
}

function analysisStructureProjection({ markdown = '', document = {}, payload = {} } = {}) {
  const declaredUnavailable = firstObject([payload.structure_unavailable, document.structure_unavailable]);
  if (resolveText(declaredUnavailable.reason)) return { structure_unavailable: declaredUnavailable };
  const outline = resolveAnalysisOutline({ markdown, document, payload });
  if (Object.keys(outline).length > 0) {
    return { outline };
  }
  const unavailable = normalizeStructureUnavailable(
    firstValue([payload.structure_unavailable, payload.structureUnavailable]),
  );
  return {
    structure_unavailable: Object.keys(unavailable).length > 0 ? unavailable : {
      reason: markdown ? 'no_markdown_headings' : 'no_analysis_outline',
      source: 'analysis_projection',
    },
  };
}

export function normalizeAnalysisPayload(value) {
  const source = resolveObject(value);
  if (Object.keys(source).length === 0) {
    return null;
  }
  const markdown = normalizeAnalysisMarkdown(firstValue([source.markdown, source.content, source.text]));
  const document = resolveObject(source.document);
  const hasDocument = isObjectValue(source.document);
  if (!markdown && !hasDocument) {
    return null;
  }
  const structure = analysisStructureProjection({ markdown, document, payload: source });
  return {
    ...source,
    markdown,
    ...(hasDocument ? { document } : {}),
    ...structure,
  };
}

export function isAnalysisArtifact(value) {
  const item = resolveObject(value);
  const activeTab = resolveText(item.active_tab);
  const documentKind = firstText([
    item.document_kind,
    item.artifact_type,
    item.kind,
  ]).toLowerCase();
  return activeTab === 'analysis' || ['analysis', 'report'].some((kind) => documentKind.includes(kind));
}

function artifactPayloadCandidates(item = {}) {
  const artifactDocument = resolveObject(firstValue([item.artifact_document, item.artifactDocument]));
  const latestRevision = resolveObject(firstValue([item.latest_revision, artifactDocument.latest_revision]));
  const document = resolveObject(firstValue([item.document, artifactDocument.document]));
  const markdown = firstValue([
    item.content,
    item.content_snapshot,
    item.summary,
    artifactDocument.content,
    artifactDocument.content_snapshot,
    artifactDocument.summary,
    latestRevision.content,
  ]);
  const documentProjection = Object.keys(document).length > 0 ? { document } : {};
  return [
    item.payload,
    item,
    { ...artifactDocument, markdown, ...documentProjection },
    {
      artifact_id: firstValue([item.artifact_id, artifactDocument.artifact_id]),
      artifact_type: firstValue([
        item.artifact_type,
        item.document_kind,
        artifactDocument.artifact_type,
      ]),
      markdown,
      title: firstValue([item.title, artifactDocument.title]),
      ...documentProjection,
    },
  ];
}

export function normalizeAnalysisArtifactPayload(value) {
  const item = resolveObject(value);
  return artifactPayloadCandidates(item)
    .map(normalizeAnalysisPayload)
    .find(Boolean) ?? null;
}
